import { useEffect, useMemo, useState } from 'react';
import adminApiService from '../services/api';
import {
  BillingQuotaConfigResponse,
  PlanConfig,
  PlanQuotaConfigItem,
  TokenPricingItem,
  PlanBillingMappingItem,
} from '../types';

const defaultQuotaType = 'rolling_6h_from_monthly_divisor';

export default function BillingQuotaConfigPage() {
  const [data, setData] = useState<BillingQuotaConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [planForm, setPlanForm] = useState({
    code: '',
    displayName: '',
    monthlyPriceUsd: '0.00',
    sortOrder: 1,
    isActive: true,
  });
  const [quotaForm, setQuotaForm] = useState({
    planId: '',
    monthlyLimitUsd: '2.00',
    windowDivisor: 120,
    periodType: defaultQuotaType,
    isActive: true,
  });
  const [pricingForm, setPricingForm] = useState({
    provider: 'claude',
    model: '',
    tokenType: 'input',
    pricePer1mTokensUsd: '0.000000',
    isActive: true,
  });
  const [mappingForm, setMappingForm] = useState({
    planId: '',
    provider: 'paypal',
    environment: 'sandbox' as 'sandbox' | 'production',
    paypalBillingPlanId: '',
    paypalProductId: '',
    isActive: true,
  });

  const planById = useMemo(() => {
    const map = new Map<string, PlanConfig>();
    (data?.plans || []).forEach((plan) => map.set(plan.id, plan));
    return map;
  }, [data]);

  const reload = async () => {
    try {
      setLoading(true);
      const result = await adminApiService.getBillingQuotaConfig();
      setData(result);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const onSavePlan = async () => {
    await adminApiService.savePlan(planForm);
    await reload();
  };

  const onSaveQuota = async () => {
    await adminApiService.saveQuotaConfig(quotaForm);
    await reload();
  };

  const onSavePricing = async () => {
    await adminApiService.saveTokenPricing(pricingForm);
    await reload();
  };

  const onSaveMapping = async () => {
    await adminApiService.saveBillingMapping(mappingForm);
    await reload();
  };

  if (loading) return <div className="p-6">Loading configuration...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="space-y-8 px-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing & Quotas</h1>
        <p className="text-sm text-gray-600 mt-1">Manage plans, quota limits, token prices, and PayPal billing plan mappings.</p>
      </div>

      <section className="bg-white border rounded p-4 space-y-3">
        <h2 className="font-semibold">Plans</h2>
        <div className="grid grid-cols-5 gap-2">
          <input className="border rounded px-2 py-1" placeholder="code" value={planForm.code} onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="display name" value={planForm.displayName} onChange={(e) => setPlanForm({ ...planForm, displayName: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="monthly price" value={planForm.monthlyPriceUsd} onChange={(e) => setPlanForm({ ...planForm, monthlyPriceUsd: e.target.value })} />
          <input className="border rounded px-2 py-1" type="number" placeholder="sort" value={planForm.sortOrder} onChange={(e) => setPlanForm({ ...planForm, sortOrder: Number(e.target.value) })} />
          <button className="bg-blue-600 text-white rounded px-3 py-1" onClick={() => void onSavePlan()}>Save Plan</button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.plans || []).map((p) => (
            <li key={p.id}>{p.code} - {p.displayName} - ${p.monthlyPriceUsd}</li>
          ))}
        </ul>
      </section>

      <section className="bg-white border rounded p-4 space-y-3">
        <h2 className="font-semibold">Plan Quota Config</h2>
        <div className="grid grid-cols-5 gap-2">
          <select className="border rounded px-2 py-1" value={quotaForm.planId} onChange={(e) => setQuotaForm({ ...quotaForm, planId: e.target.value })}>
            <option value="">Select plan</option>
            {(data?.plans || []).map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
          </select>
          <input className="border rounded px-2 py-1" placeholder="monthly USD limit" value={quotaForm.monthlyLimitUsd} onChange={(e) => setQuotaForm({ ...quotaForm, monthlyLimitUsd: e.target.value })} />
          <input className="border rounded px-2 py-1" type="number" placeholder="window divisor" value={quotaForm.windowDivisor} onChange={(e) => setQuotaForm({ ...quotaForm, windowDivisor: Number(e.target.value) })} />
          <input className="border rounded px-2 py-1" value={quotaForm.periodType} onChange={(e) => setQuotaForm({ ...quotaForm, periodType: e.target.value })} />
          <button className="bg-blue-600 text-white rounded px-3 py-1" onClick={() => void onSaveQuota()}>Save Quota</button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.quotaConfigs || []).map((q: PlanQuotaConfigItem) => (
            <li key={q.id}>{planById.get(q.planId)?.code || q.planId} - ${q.monthlyLimitUsd} / 120 = ${(Number(q.monthlyLimitUsd) / q.windowDivisor).toFixed(6)}</li>
          ))}
        </ul>
      </section>

      <section className="bg-white border rounded p-4 space-y-3">
        <h2 className="font-semibold">Token Pricing</h2>
        <div className="grid grid-cols-5 gap-2">
          <input className="border rounded px-2 py-1" placeholder="provider" value={pricingForm.provider} onChange={(e) => setPricingForm({ ...pricingForm, provider: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="model" value={pricingForm.model} onChange={(e) => setPricingForm({ ...pricingForm, model: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="token type" value={pricingForm.tokenType} onChange={(e) => setPricingForm({ ...pricingForm, tokenType: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="price per 1M USD" value={pricingForm.pricePer1mTokensUsd} onChange={(e) => setPricingForm({ ...pricingForm, pricePer1mTokensUsd: e.target.value })} />
          <button className="bg-blue-600 text-white rounded px-3 py-1" onClick={() => void onSavePricing()}>Save Pricing</button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.tokenPricing || []).map((p: TokenPricingItem) => (
            <li key={p.id}>{p.provider} / {p.model} / {p.tokenType} = ${p.pricePer1mTokensUsd}</li>
          ))}
        </ul>
      </section>

      <section className="bg-white border rounded p-4 space-y-3">
        <h2 className="font-semibold">PayPal billing plan mapping</h2>
        <div className="grid grid-cols-6 gap-2">
          <select className="border rounded px-2 py-1" value={mappingForm.planId} onChange={(e) => setMappingForm({ ...mappingForm, planId: e.target.value })}>
            <option value="">Select plan</option>
            {(data?.plans || []).map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
          </select>
          <select className="border rounded px-2 py-1" value={mappingForm.environment} onChange={(e) => setMappingForm({ ...mappingForm, environment: e.target.value as 'sandbox' | 'production' })}>
            <option value="sandbox">sandbox</option>
            <option value="production">production</option>
          </select>
          <input className="border rounded px-2 py-1" value={mappingForm.provider} onChange={(e) => setMappingForm({ ...mappingForm, provider: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="P- billing plan id" value={mappingForm.paypalBillingPlanId} onChange={(e) => setMappingForm({ ...mappingForm, paypalBillingPlanId: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="PROD- product (opt)" value={mappingForm.paypalProductId} onChange={(e) => setMappingForm({ ...mappingForm, paypalProductId: e.target.value })} />
          <button className="bg-blue-600 text-white rounded px-3 py-1" onClick={() => void onSaveMapping()}>Save Mapping</button>
        </div>
        <ul className="text-sm space-y-1">
          {(data?.billingMappings || []).map((m: PlanBillingMappingItem) => (
            <li key={m.id}>{planById.get(m.planId)?.code || m.planId} / {m.environment} = {m.paypalBillingPlanId}{m.paypalProductId ? ` (${m.paypalProductId})` : ''}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

